import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import './i18n/config'; // Initialize i18next
import { FranchiseProvider } from './contexts/FranchiseContext';
import { BranchProvider } from './contexts/BranchContext';
import { BarberProvider } from './contexts/BarberContext';
import { ServiceProvider } from './contexts/ServiceContext';
import { QueueProvider } from './contexts/QueueContext';
import { OfferProvider } from './contexts/OfferContext';
import { AppointmentProvider } from './contexts/AppointmentContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { NotificationPermissionBanner } from './components/notifications/NotificationPermissionBanner';
import { CookieBanner } from './components/common/CookieBanner';
import AppDownloadBanner from './components/common/AppDownloadBanner';
import { GlobalModals } from './components/modals/GlobalModals';
import { MobileRouteGuard } from './components/mobile/MobileRouteGuard';
import Home from './pages/Home';
import About from './pages/public/About';
import PublicServices from './pages/public/PublicServices';
import Contact from './pages/public/Contact';
import Blog from './pages/public/Blog';
import CookiesPolicy from './pages/legal/CookiesPolicy';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import DownloadAppPage from './pages/DownloadAppPage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ForceLogout from './pages/ForceLogout';
import VerifyEmailPage from './pages/VerifyEmailPage';
import Dashboard from './pages/Dashboard';
import FranchisesPage from './pages/FranchisesPage';
import FranchiseFormPage from './pages/FranchiseFormPage';
import BranchesPage from './pages/BranchesPage';
import BranchFormPage from './pages/BranchFormPage';
import BarbersPage from './pages/BarbersPage';
import BarberFormPage from './pages/BarberFormPage';
// BarberManagementPage removed - functionality moved to AdminPanel/BarbersManagement
import ServicesPage from './pages/ServicesPage';
import ServiceFormPage from './pages/ServiceFormPage';
import QueuePage from './pages/QueuePage';
import TakeTicketPage from './pages/TakeTicketPage';
import ClientQueue from './pages/ClientQueue';
import { OvertimeWaitingPage } from './pages/OvertimeWaitingPage';
import BarberQueue from './pages/BarberQueue';
import BarberHistoryPage from './pages/BarberHistoryPage';
import BarberPaymentsPage from './pages/BarberPaymentsPage';
import ProfilePage from './pages/ProfilePage';
import UserDashboard from './pages/user/UserDashboard';
import LoyaltyPage from './pages/LoyaltyPage';
import HelpPage from './pages/HelpPage';
import OffersPage from './pages/OffersPage';
import CreateOfferPage from './pages/CreateOfferPage';
import EditOfferPage from './pages/EditOfferPage';
import FeedPage from './pages/FeedPage';
import CreatePostPage from './pages/CreatePostPage';
import EditPostPage from './pages/EditPostPage';
import PostNotificationsPage from './pages/PostNotificationsPage';
import AdminPostPermissionsPage from './pages/AdminPostPermissionsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import BookAppointmentPage from './pages/BookAppointmentPage';
import AppointmentDetailsPage from './pages/AppointmentDetailsPage';
import LandingConfigPage from './pages/LandingConfigPage';
import ImageOptimizationAdmin from './pages/admin/ImageOptimizationAdmin';
import AdminPanel from './pages/admin/AdminPanel';
import AdminBillingPage from './pages/AdminBillingPage';
import JobApplicationsPage from './pages/admin/JobApplicationsPage';
import FranchiseApplicationsPage from './pages/admin/FranchiseApplicationsPage';
import ContentReportsPage from './pages/ContentReportsPage';
import QueueSelectorPage from './pages/public/QueueSelectorPage';
import QueueDisplayPage from './pages/public/QueueDisplayPage';
import NotFound from './pages/NotFound';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { setupPushNotificationListeners } from './lib/pushNotifications';

// Screens where the Android back button should leave the app instead of
// navigating back through history (Android convention: back on the app's
// main screen sends it to the background).
const BACK_EXIT_SCREENS = new Set([
  '/',
  '/login',
  '/client-queue',
  '/barber-queue',
  '/dashboard',
  '/take-ticket',
  '/user/dashboard',
]);

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Register notification tap listeners as early as possible so taps that
      // cold-start the app (retained events) are consumed and navigate correctly.
      // Wrapped so a push-setup failure can never prevent back-button handling.
      try {
        setupPushNotificationListeners();
      } catch (e) {
        console.error('[App] Push listener setup failed:', e);
      }

      // Explicit Android back button handling. Without a listener, Capacitor
      // walks the whole webview history and redirect loops (e.g. /login
      // re-pushing /dashboard) made it impossible to ever leave the app.

      // Guard for bounce detection: when going back crosses a full document
      // load (e.g. history entries created by notification-tap navigations),
      // the path doesn't change before the timer fires but the page IS
      // navigating — don't minimize in that case.
      let pageUnloading = false;
      window.addEventListener('pagehide', () => { pageUnloading = true; });

      const listenerPromise = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        const path = window.location.pathname;
        console.log(`[App] backButton: path=${path} canGoBack=${canGoBack}`);

        if (BACK_EXIT_SCREENS.has(path) || !canGoBack) {
          CapacitorApp.minimizeApp().catch((e) => console.error('[App] minimizeApp failed:', e));
          return;
        }

        window.history.back();

        // Bounce detection: if going back left us on the same screen (a guard
        // redirected forward again, or the entry below is a dead duplicate),
        // the button would feel "blocked" — treat it as root and leave the app.
        setTimeout(() => {
          if (!pageUnloading && window.location.pathname === path) {
            console.log('[App] backButton bounce detected, minimizing');
            CapacitorApp.minimizeApp().catch((e) => console.error('[App] minimizeApp failed:', e));
          }
        }, 400);
      });

      listenerPromise.catch((e) => console.error('[App] backButton listener registration failed:', e));

      return () => {
        listenerPromise.then((handle) => handle.remove()).catch(() => undefined);
      };
    }

    // Web: the service worker posts NOTIFICATION_CLICK when a window is already
    // open. Without this listener the window was focused but never navigated.
    if ('serviceWorker' in navigator) {
      const onSwMessage = (event: MessageEvent) => {
        const msg = event.data;
        if (msg?.type === 'NOTIFICATION_CLICK' && typeof msg.url === 'string' && msg.url.startsWith('/')) {
          window.location.href = msg.url;
        }
      };
      navigator.serviceWorker.addEventListener('message', onSwMessage);
      return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <FranchiseProvider>
            <BranchProvider>
              <BarberProvider>
                <ServiceProvider>
                  <OfferProvider>
                    <QueueProvider>
                      <AppointmentProvider>
                        <BrowserRouter
                        future={{
                          v7_startTransition: true,
                          v7_relativeSplatPath: true,
                        }}
                      >
                    <MobileRouteGuard>
                    <NotificationPermissionBanner />
                    <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/public-services" element={<PublicServices />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/blog" element={<Blog />} />
                    {/* Legal pages */}
                    <Route path="/cookies-policy" element={<CookiesPolicy />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    {/* App Download - Smart redirect based on device */}
                    <Route path="/download" element={<DownloadAppPage />} />
                    <Route path="/app" element={<DownloadAppPage />} />
                    <Route path="/get-app" element={<DownloadAppPage />} />
                    <Route path="/descargar" element={<DownloadAppPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/force-logout" element={<ForceLogout />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <Dashboard />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Franchises */}
                    <Route
                      path="/franchises"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FranchisesPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/franchises/new"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FranchiseFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/franchises/:id/edit"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FranchiseFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Branches */}
                    <Route
                      path="/branches"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BranchesPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/branches/new"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BranchFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/branches/:id/edit"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BranchFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Barbers */}
                    <Route
                      path="/barbers"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarbersPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/barbers/new"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarberFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/barbers/:id/edit"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarberFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin specific routes - MUST be before /admin/:section? */}
                    {/* Landing Config */}
                    <Route
                      path="/admin/landing-config"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <LandingConfigPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Image Optimization Admin */}
                    <Route
                      path="/admin/image-optimization"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ImageOptimizationAdmin />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Billing */}
                    <Route
                      path="/admin/billing"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AdminBillingPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Job Applications */}
                    <Route
                      path="/admin/job-applications"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <JobApplicationsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Franchise Applications */}
                    <Route
                      path="/admin/franchise-applications"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FranchiseApplicationsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Post Permissions */}
                    <Route
                      path="/admin/post-permissions"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AdminPostPermissionsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Content Reports (Super Admin) */}
                    <Route
                      path="/admin/reports"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ContentReportsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Panel (Super Admin) - generic route with optional section */}
                    {/* This catches /admin, /admin/overview, /admin/barbers, /admin/services, etc. */}
                    <Route
                      path="/admin/:section?"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AdminPanel />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Services */}
                    <Route
                      path="/services"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ServicesPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/services/new"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ServiceFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/services/:id/edit"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ServiceFormPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Queue */}
                    <Route
                      path="/queue"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <QueuePage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/take-ticket"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <TakeTicketPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/client-queue"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ClientQueue />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/overtime-waiting"
                      element={
                        <ProtectedRoute>
                          <OvertimeWaitingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/barber-queue"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarberQueue />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/barber-history"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarberHistoryPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/barber-payments"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BarberPaymentsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Profile */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ProfilePage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* User Dashboard */}
                    <Route
                      path="/user/dashboard"
                      element={
                        <ProtectedRoute>
                          <UserDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Loyalty */}
                    <Route
                      path="/loyalty"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <LoyaltyPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Help */}
                    <Route
                      path="/help"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <HelpPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Offers */}
                    <Route
                      path="/offers"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <OffersPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/offers/new"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <CreateOfferPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/offers/:offerId/edit"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <EditOfferPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Feed */}
                    <Route
                      path="/feed"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FeedPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Create Post */}
                    <Route
                      path="/feed/create"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <CreatePostPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Edit Post */}
                    <Route
                      path="/feed/edit/:postId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <EditPostPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Post Notifications */}
                    <Route
                      path="/notifications/posts"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <PostNotificationsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Appointments */}
                    <Route
                      path="/appointments"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AppointmentsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/appointments/book"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <BookAppointmentPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/appointments/:appointmentId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <AppointmentDetailsPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Public Queue Display (no auth required) */}
                    <Route path="/queues" element={<QueueSelectorPage />} />
                    <Route path="/:franchiseId/queues" element={<QueueDisplayPage />} />

                    {/* 404 Not Found */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                          <CookieBanner />
                          <AppDownloadBanner />
                    </MobileRouteGuard>
                        </BrowserRouter>
                        <Toaster position="top-right" />
                        <GlobalModals />
                      </AppointmentProvider>
                    </QueueProvider>
                  </OfferProvider>
                </ServiceProvider>
              </BarberProvider>
            </BranchProvider>
          </FranchiseProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
